import { useEffect } from "react";
import { WidgetSpotlightProvider } from "@/contexts/WidgetSpotlightContext";
import DemoWelcomeModal from "./DemoWelcomeModal";
import GuideRail from "./GuideRail";
import DemoChecklistLauncher from "./checklist/DemoChecklistLauncher";
import { demoTemplateFor } from "./templates";
import { applyDemoTemplate } from "./applyDemoTemplate";
import { demoWatchlistFor } from "./demoWatchlistFor";
import { setDemoWatchlist } from "@/pages/PortfolioBuilder/demoWatchlist";
import { visibleSteps } from "./visibleSteps";
import {
  getDemoGuideDevOverride,
  setDemoGuideDevOverride,
  useDemoGuideGate,
} from "./useDemoGuideGate";
import { useDemoGuideStore } from "./useDemoGuideStore";
import { useStepDetection } from "./useStepDetection";

/**
 * Single mount point for all guided-demo UI, rendered once in App.jsx beside
 * OnboardingGate.
 *
 * POPOUT SAFETY RULE: as with src/onboarding/, nothing under src/demo-guide/
 * may be imported from src/widget-components/**. Popout windows portal
 * componentMap widgets and must never render this tree.
 *
 * The two gates cannot both fire: onboarding requires authenticated +
 * in_trial, this requires logged out. See __tests__/gateExclusivity.test.ts.
 */
export function DemoGuideGate({ children }: { children: React.ReactNode }) {
  const { phase, showChecklist } = useDemoGuideGate();
  const { state, begin, reset } = useDemoGuideStore((s) => ({
    state: s.state,
    begin: s.begin,
    reset: s.reset,
  }));

  const active = phase !== "ineligible";

  // Dev/testing: ?demo-guide=reset wipes local state and re-runs from the
  // picker. Unlike onboarding's equivalent this needs no server round-trip.
  useEffect(() => {
    if (getDemoGuideDevOverride() !== "reset") return;
    reset();
    setDemoGuideDevOverride("force");
  }, [reset]);

  // Record the arrival the moment the picker is due, so "saw it" and
  // "answered it" stay separable in the funnel.
  useEffect(() => {
    if (phase === "picker") begin();
  }, [phase, begin]);

  useStepDetection(active);

  /**
   * Rebuild the asset class's dashboard and watchlist after a reload.
   *
   * pickAssetClass does both, but that runs once and the selection outlives it
   * in localStorage. Nothing carries the layout across a reload: the tab
   * store's persist subscription returns early unless `tabsLoaded`, and that
   * only goes true after a successful AUTHENTICATED read of the server layout
   * — so for a logged-out visitor it is false forever and no write is ever
   * attempted. That gate is load-bearing (persisting pre-load tabs would
   * overwrite a real saved layout, which pref-storage cannot recover), so the
   * fix belongs here rather than there.
   *
   * Without this the run survived a reload but the dashboard did not:
   * getLoggedOutTabs() defaults came back, and the rail carried on ringing
   * `filteredHeadlinesId` — a widget id no longer on the page — so the steps
   * pointed at nothing. Worst through the checklist's "resume", where the run
   * continues mid-flow over a layout that never matched it.
   *
   * Rebuilding rather than persisting is deliberate. It costs a filter or
   * highlight applied mid-run, which is already lost today because the whole
   * layout resets; the alternative is a store of its own for state that is
   * disposable by design. applyDemoTemplate replaces the active tab wholesale
   * and is idempotent, so running it again here is safe, including alongside
   * the call pickAssetClass just made.
   *
   * Keyed on the asset class, so applying a highlight later does not re-run it
   * and wipe what was just applied.
   */
  const pickedAssetClass = active ? state?.assetClass : undefined;
  useEffect(() => {
    if (!pickedAssetClass) return;
    const template = demoTemplateFor(pickedAssetClass);
    if (template) applyDemoTemplate(template);
    // Same resolver pickAssetClass uses. Resolving it independently here is how the
    // reload path came to install the asset-class template's list while the run was
    // asking them to apply the recorded plan's.
    const spec = demoWatchlistFor(pickedAssetClass);
    if (spec) setDemoWatchlist(spec);
  }, [pickedAssetClass]);

  // Which widget the rail is pointing at. Resolved here rather than in the
  // rail so the provider wraps the dashboard whether or not the rail renders.
  //
  // "filtered" resolves through the applied template, so the ring follows the
  // narrowed Headlines instance for whichever asset class they picked without any
  // id being written down twice.
  let targetWidgetId: string | null = null;
  let targetControl: "highlight" | "filter" | "popout" | null = null;
  if (phase === "run" && state?.assetClass && !state.completedAt) {
    const step = visibleSteps(state.assetClass)[state.step];
    const template = demoTemplateFor(state.assetClass);
    targetControl = step?.control ?? null;
    if (step?.target === "filtered") {
      targetWidgetId = template?.filteredHeadlinesId ?? null;
    } else if (step?.target === "primary") {
      targetWidgetId = "NewsquawkHeadlines-primary";
    } else {
      targetWidgetId = step?.target ?? null;
    }
  }

  if (!active) return <>{children}</>;

  return (
    <WidgetSpotlightProvider
      targetWidgetId={targetWidgetId}
      targetControl={targetControl}
    >
      {children}
      {phase === "picker" && <DemoWelcomeModal />}
      {phase === "run" && <GuideRail />}
      {showChecklist && phase !== "run" && <DemoChecklistLauncher />}
      {import.meta.env.DEV && (
        <div
          className="hidden"
          data-demo-guide-phase={phase}
          data-demo-guide-target={targetWidgetId ?? ""}
        />
      )}
    </WidgetSpotlightProvider>
  );
}
